<template>
  <div>
    <v-overlay
      v-if="!storeDataReady"
      :model-value="true"
      scrim="primary"
      opacity=".95"
      class="align-center justify-center"
    >
      <div class="text-center">
        <v-progress-circular color="accent" indeterminate />
        <p class="mt-6">Loading...</p>
      </div>
    </v-overlay>
    <LoadingOverlay v-if="versionAction" scrim="grey-darken-2" :message="versionAction" />
    <div id="quote-edit" v-if="quote !== null && selectedVersion !== null && storeDataReady">
      <div id="view-nav">
        <v-tabs
          v-if="!quote.versions[selectedVersion].published"
          v-model="currentTab"
          :height="60"
          bg-color="primary"
          slider-color="accent"
          @update:model-value="onTabChange"
        >
          <v-tab
            v-for="(tab, key) in tabs"
            :disabled="(key == 1 && !materialHandleSet) || (tab.excludeUS && quote.region == 'us')"
            :key="key"
          >
            {{ tab.title }}</v-tab
          >
        </v-tabs>
        <v-tabs v-else :height="60" bg-color="primary" slider-color="accent" @update:model-value="onTabChange">
          <v-tab :key="0">Quote</v-tab>
          <v-tab :key="1">Visual</v-tab>
          <v-tab :key="2">CAD</v-tab>
        </v-tabs>
        <div id="page-actions" class="d-flex align-center" v-if="currentTab == 0">
          <v-icon v-if="unsavedChanges" :size="20" class="mr-2">mdi-content-save</v-icon>
          <v-select
            theme="dark"
            :list-props="{ theme: 'light' }"
            density="compact"
            hide-details
            v-model="selectedVersion"
            :items="versionNames"
            item-title="text"
            class="mr-2 text-body-2"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props" :title="undefined">
                <div class="d-flex" :style="{ width: '100%' }">
                  <v-icon v-if="item.raw.versionCheckComplete" size="small" class="mr-1">mdi-thumb-up</v-icon>
                  <v-icon v-else-if="item.raw.readyToCheck" size="small" class="mr-1">mdi-eye</v-icon>
                  <div class="text-caption">{{ item.raw.text }}</div>
                  <v-icon class="ml-auto" v-if="item.raw.hasPayment" size="small">mdi-cart</v-icon>
                </div>
              </v-list-item>
            </template>
          </v-select>
          <v-icon :size="20" class="mx-2" @click="editVersionOrderInfo">mdi-playlist-edit</v-icon>
          <v-icon
            :ripple="false"
            :size="20"
            class="mx-2"
            @click="
              () => {
                tmpValue = quote.versions[selectedVersion].title || ''
                tmpDesc = quote.versions[selectedVersion].titleDesc || ''
                renameDialog = !renameDialog
              }
            "
            >mdi-format-title</v-icon
          >
          <v-icon :size="17" class="mx-2" @click="duplicateSelectedVersion">mdi-content-copy</v-icon>
          <v-icon :size="19" class="mx-2" @click="addEmptyVersion">mdi-file-plus</v-icon>
          <v-icon
            v-if="quote.versions[selectedVersion].currency && quote.versions[selectedVersion].currency !== 'GBP'"
            :size="19"
            class="mx-2"
            :disabled="fetchingFxRate"
            @click="openRefreshFxDialog"
            title="Refresh exchange rate"
            >mdi-currency-eur</v-icon
          >
          <v-icon
            class="mx-2"
            v-if="!order"
            :size="22"
            :disabled="quote.versions.length == 1"
            @click="deleteVersionDialog = true"
            >mdi-delete</v-icon
          >
          <!-- Version Check Icon -->
          <v-icon :size="20" class="mx-2" @click="versionCheckDialog = true">mdi-eye</v-icon>
          <v-btn
            class="ml-2"
            v-if="
              !quote.versions[selectedVersion].published &&
              ['sho@plykea.com', 'kath@plykea.com', 'adam@plykea.com', 'laura@plykea.com', 'kate@plykea.com'].includes(
                $store.state.user.email
              )
            "
            size="small"
            variant="flat"
            color="teal"
            @click="scannerOverlayVisible = true"
            >Scanner</v-btn
          >
          <v-btn
            class="ml-2"
            v-if="!quote.versions[selectedVersion].published"
            size="small"
            variant="flat"
            :color="'info'"
            :disabled="unsavedChanges"
            @click="tryOpenVisual"
            >Visual</v-btn
          >
          <v-btn
            class="ml-2 mr-4"
            v-if="!quote.versions[selectedVersion].published"
            size="small"
            variant="flat"
            :color="'accent'"
            :disabled="unsavedChanges"
            @click="publishSelectedVersion"
            >Publish</v-btn
          >
          <v-btn
            class="ml-2 mr-4"
            v-else
            size="small"
            variant="flat"
            :color="order ? 'warning' : 'accent'"
            @click="order ? openEditOrderDialog() : unpublishSelectedVersion()"
            >{{ order ? 'Edit Order' : 'Unpublish' }}</v-btn
          >
        </div>
      </div>

      <!-- Published Quote Sub Menu -->
      <div id="view-sub-nav" v-if="quote.versions[selectedVersion].published && currentTab == 0">
        <v-btn
          class="mr-2"
          variant="outlined"
          color="primary"
          v-if="quote.versions[selectedVersion].published"
          @click="
            () => {
              tmpValue = quote.versions[selectedVersion].notes
              editNotesDialog = true
            }
          "
          >Edit Notes</v-btn
        >
        <v-btn class="mx-2" variant="outlined" color="primary" @click="copyCustomerQuoteLink">Get Quote Link</v-btn>
        <template v-if="order && quote.region === 'eu'">
          <v-btn v-if="order.xeroInvoiceNumber" class="mx-2" variant="outlined" color="primary" @click="copyInvoiceLink"
            >Get Invoice Link</v-btn
          >
          <v-btn v-else-if="orderFullyPaid" class="mx-2" variant="outlined" color="primary" @click="generateEuInvoice"
            >Generate EU Invoice</v-btn
          >
        </template>
        <v-spacer />
        <v-btn
          v-if="!order || (!order.foc && order.balanceOutstanding)"
          class="mx-2"
          variant="outlined"
          color="primary"
          @click="paymentTypeDialog = true"
          >Set payment type</v-btn
        >
        <v-btn
          v-if="quote.versions[selectedVersion].foc && !order"
          class="mr-4"
          variant="outlined"
          color="primary"
          @click="placeOrder"
          >Place Order</v-btn
        >
        <v-btn v-if="order" class="mr-2" variant="outlined" color="primary" @click="recutDialog = true"
          >Create Recut</v-btn
        >
      </div>

      <transition name="fade" mode="out-in">
        <component
          ref="currentTabComponent"
          :is="quote.versions[selectedVersion].published ? publishedComponent : tabs[currentTab].tabComponent"
          :quote="quote"
          :order="order"
          v-model:version="quote.versions[selectedVersion]"
          :customerEmail="quote.customerEmail"
          :pricing="draftPricing"
          :config="tabs[currentTab]"
          :key="currentTab"
          @printableSVG="printableSVG"
          @customerVisualCreated="saveVisualId"
          @uidsUpdated="unsavedChanges = true"
          @draftQuoteUpdated="ondraftQuoteUpdated"
          @openVisual="openVisualTab"
        ></component>
      </transition>

      <!-- Notes dialog -->
      <v-dialog v-model="editNotesDialog" width="500">
        <v-card>
          <v-card-text class="pt-4">
            <v-textarea variant="outlined" label="Order notes" hide-details v-model="tmpValue"></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="editNotesDialog = false">Cancel</v-btn>
            <v-btn color="accent" variant="flat" @click="updateNotes">Done</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Recut Dialog -->
      <v-dialog v-model="recutDialog" width="800">
        <RecutTracker
          v-if="recutDialog"
          :quote="quote"
          :version="quote.versions[selectedVersion]"
          @cancel="recutDialog = false"
          @complete="recutComplete"
        ></RecutTracker>
      </v-dialog>

      <!-- Visual Blocked Dialog -->
      <v-dialog v-model="visualBlockedDialog" width="500">
        <v-card>
          <v-card-title class="bg-warning text-white">Cannot Open Visual</v-card-title>
          <v-card-text class="pt-4">
            <p>Please fix the following issues before opening the Visual editor:</p>
            <ul class="mt-2">
              <li v-for="(reason, idx) in visualBlockedReasons" :key="idx" class="mb-1">
                {{ reason }}
              </li>
            </ul>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="flat" @click="visualBlockedDialog = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Scanner Fullscreen -->
      <!-- 1500, not 100, for the same reason as the Visual overlay below. -->
      <v-card
        v-if="scannerOverlayVisible"
        style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 1500; overflow-y: auto"
      >
        <v-toolbar color="primary">
          <v-btn icon @click="scannerOverlayVisible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>IKEA Scanner</v-toolbar-title>
          <v-spacer />
          <v-btn
            size="small"
            variant="flat"
            color="accent"
            :disabled="!$refs.scannerRef || !$refs.scannerRef.selectedCount || !$refs.scannerRef.allSelectedValid"
            @click="$refs.scannerRef.convertToFronts()"
          >
            Convert
          </v-btn>
        </v-toolbar>
        <Scanner
          ref="scannerRef"
          :quote="quote"
          :version="quote.versions[selectedVersion]"
          :config="{}"
          @converted="onScannerConverted"
        />
      </v-card>

      <!-- Visual Fullscreen -->
      <v-card v-if="visualOverlayVisible" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 1500">
        <v-toolbar color="primary">
          <v-btn icon @click="closeVisualOverlay" :disabled="visualSaving">
            <v-progress-circular v-if="visualSaving" indeterminate size="24" width="2"></v-progress-circular>
            <v-icon v-else>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Visual Editor</v-toolbar-title>
        </v-toolbar>
        <CustomerCad
          ref="visualCad"
          :quote="quote"
          :version="quote.versions[selectedVersion]"
          :headerOffset="124"
          @customerVisualCreated="saveVisualId"
          @visualLoadError="handleVisualLoadError"
        />
      </v-card>

      <!-- Rename dialog -->
      <v-dialog v-model="renameDialog" persistent width="500">
        <v-card>
          <v-card-title class="bg-primary text-white">Rename Version</v-card-title>
          <v-card-text class="pt-6">
            <v-text-field label="Version name" hide-details v-model="tmpValue"></v-text-field>
            <v-text-field label="Version description" hide-details v-model="tmpDesc" class="mt-4"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="renameDialog = false">Cancel</v-btn>
            <v-btn color="accent" variant="flat" @click="updateVersionTitle">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Rename dialog -->
      <v-dialog v-model="deleteVersionDialog" persistent width="500">
        <v-card>
          <v-card-title class="bg-warning text-white text-h6">Delete Version</v-card-title>
          <v-card-text class="pt-4"
            >Are you sure you want to delete this version? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="deleteVersionDialog = false">Cancel</v-btn>
            <v-btn color="warning" variant="flat" @click="deleteVersion">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="refreshFxDialog" persistent width="500">
        <v-card>
          <v-card-title class="bg-primary text-white text-h6">Refresh Exchange Rate</v-card-title>
          <v-card-text class="pt-4">
            <p class="mb-2">Replace the rate locked on this version with today's live rate?</p>
            <v-table density="compact">
              <tbody>
                <tr>
                  <td><strong>Current</strong></td>
                  <td>
                    {{ currentFxRateDisplay }}
                    <span class="text-grey-darken-1"> ({{ currentFxRateDate }}) </span>
                  </td>
                </tr>
                <tr>
                  <td><strong>New (live)</strong></td>
                  <td>
                    {{ pendingFxRateDisplay }}
                    <span class="text-grey-darken-1"> ({{ pendingFxRateDate }}) </span>
                  </td>
                </tr>
              </tbody>
            </v-table>
            <p class="mt-3 text-caption text-grey-darken-1">Rate already includes the 2.5% surcharge.</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="refreshFxDialog = false">Cancel</v-btn>
            <v-btn color="primary" variant="flat" @click="confirmRefreshExchangeRate">Confirm</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Version & Order Info Dialog -->
      <v-dialog v-model="editVersionOrderDialog" persistent width="600">
        <v-card>
          <v-card-title class="bg-primary text-white text-h6">Edit Version & Order Info</v-card-title>
          <v-card-text class="pt-6">
            <div class="mb-4">
              <div class="text-body-1 font-weight-medium mb-2">
                Editing: <span class="text-primary">{{ quote.versions[selectedVersion].title }}</span>
              </div>
              <div v-if="orderNumber" class="text-body-2 mb-3">
                Order Number: <span class="font-weight-medium">{{ orderNumber }}</span>
              </div>
              <v-alert type="info" density="compact" class="mb-4">
                Changes will update the version creator email and the associated order customer email.
                <br />Note: Quote-level customer email can be edited in the quote list view.
              </v-alert>
            </div>

            <v-form ref="editVersionOrderForm" validate-on="blur">
              <v-combobox
                v-model="editVersionOrderForm.creatorEmail"
                :items="creatorEmailOptions"
                label="Version Creator Email"
                :rules="[$rules.required('creatorEmail'), $rules.email('creatorEmail')]"
                required
                class="mb-4"
              ></v-combobox>

              <v-text-field
                v-if="editingOrderData"
                v-model="editVersionOrderForm.customerEmail"
                label="Order Customer Email"
                :rules="[$rules.required('orderCustomerEmail'), $rules.email('orderCustomerEmail')]"
                required
                class="mb-2"
              ></v-text-field>

              <v-text-field
                v-if="editingOrderData"
                v-model="editVersionOrderForm.confirmCustomerEmail"
                label="Confirm Order Customer Email"
                :rules="[$rules.required('confirmOrderCustomerEmail'), $rules.email('confirmOrderCustomerEmail')]"
                :error-messages="confirmEmailErrors"
                required
                class="mb-4"
              ></v-text-field>

              <div v-else class="text-body-2 text-medium-emphasis mb-4">
                No associated order found for this version.
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn variant="text" @click="editVersionOrderDialog = false">Cancel</v-btn>
            <v-btn variant="flat" color="accent" @click="saveVersionOrderInfo" :disabled="!canSaveVersionOrder"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Version Check Dialog -->
      <v-dialog v-model="versionCheckDialog" persistent width="500">
        <v-card>
          <v-card-title class="bg-primary text-white">Version Check</v-card-title>
          <v-card-text class="pt-6">
            <v-switch
              v-model="localReadyToCheck"
              hide-details
              label="Version requires check"
              class="pt-0 mt-0 mb-4"
              @update:model-value="handleReadyToCheckToggle"
              :disabled="localBypassVersionCheck"
            ></v-switch>
            <v-checkbox
              v-if="localReadyToCheck"
              v-model="localVersionCheckComplete"
              hide-details
              label="Version check complete"
              class="pt-0 mt-0 mb-4"
            ></v-checkbox>
            <v-divider class="my-4"></v-divider>
            <v-checkbox
              v-model="localBypassVersionCheck"
              hide-details
              label="Bypass version check (enables order placement)"
              class="pt-0 mt-0"
              @update:model-value="handleBypassToggle"
            ></v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="flat"
              :loading="versionCheckSaving"
              :disabled="versionCheckSaving"
              @click="handleVersionCheckSave"
            >
              Done
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Edit Order Warning -->
      <v-dialog v-model="editOrderDialog" width="600" scrollable>
        <v-card>
          <v-card-title class="bg-warning text-white text-h6">Warning!</v-card-title>
          <v-card-text class="pt-4">
            <p>
              You are about to edit an order which is in production. Notify workshop if there will be changes made to
              the order.
            </p>

            <!-- Payments -->
            <div v-if="order && order.payments && order.payments.length > 0" class="mb-4">
              <v-alert type="error" density="compact" class="mb-2">Payment has been made on this order</v-alert>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (charged)</th>
                    <th>Settled (GBP)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(payment, i) in order.payments" :key="i">
                    <td>{{ payment.date ? $utils.humanFromVuetify($utils.vuetifyTimestamp(payment.date)) : '—' }}</td>
                    <td>{{ $numeral(payment.amount).format('$0,0.00') }}</td>
                    <td>
                      <span v-if="payment.charge && payment.charge.balance_transaction">
                        £{{ $numeral(payment.charge.balance_transaction.amount / 100).format('0,0.00') }}
                        <span class="text-caption text-grey"
                          >@ {{ payment.charge.balance_transaction.exchange_rate }}</span
                        >
                      </span>
                      <span v-else class="text-grey">—</span>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>

            <!-- Toolpath / CAD -->
            <div v-if="editOrderShipments && editOrderShipments.length > 0" class="mb-4">
              <div v-for="(shipment, i) in editOrderShipments" :key="'s' + i">
                <v-alert v-if="shipment.toolpathed" type="error" density="compact" class="mb-2">
                  CAD / Toolpath complete — toolpathed on
                  {{ $utils.humanFromVuetify($utils.vuetifyTimestamp(shipment.toolpathed)) }}
                  <span v-if="shipment.materialOrdered">
                    · material ordered on
                    {{ $utils.humanFromVuetify($utils.vuetifyTimestamp(shipment.materialOrdered)) }}</span
                  >
                </v-alert>
              </div>
            </div>

            <!-- Price recalculation warning -->
            <v-alert type="warning" density="compact" class="mb-2">
              If price settings have been updated since this order was placed, unpublishing will trigger an automatic
              price recalculation that may differ from what the customer agreed to pay. This cannot be reverted.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="text" @click="editOrderDialog = false">Cancel</v-btn>
            <v-btn
              color="warning"
              variant="flat"
              @click="
                () => {
                  unpublishSelectedVersion()
                  editOrderDialog = false
                }
              "
              >Continue</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Order price change dialog -->
      <v-dialog v-model="priceChangeDialog" width="600">
        <v-card v-if="this.quote.versions[this.selectedVersion].pricing && this.draftPricing">
          <v-card-title class="bg-primary text-white text-h6">Order value changed</v-card-title>
          <v-card-text class="pt-4">
            <p class="py-3 text-subtitle-2">
              Updates to this order have changed the total by
              {{
                $numeral(
                  this.$utils.toFixedNumber(
                    this.draftPricing.total - this.quote.versions[this.selectedVersion].pricing.total,
                    2
                  )
                ).format('$0,0.00')
              }}. What would you like to do?
            </p>
            <div>
              <v-btn @click="commitOrderChanges(false)" class="mb-3" block color="primary"
                >Keep original total of
                {{
                  $numeral(
                    this.$utils.toFixedNumber(this.quote.versions[this.selectedVersion].pricing.total, 2)
                  ).format('$0,0.00')
                }}</v-btn
              >
              <v-btn @click="commitOrderChanges(true)" block color="accent"
                >Update to new total of
                {{ $numeral(this.$utils.toFixedNumber(this.draftPricing.total, 2)).format('$0,0.00') }}</v-btn
              >
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-overlay v-model="generatingInvoice" scrim="primary" opacity=".95" class="align-center justify-center">
        <div class="text-center">
          <v-progress-circular color="accent" indeterminate />
          <p class="mt-6">Generating Invoice</p>
        </div>
      </v-overlay>

      <SetPaymentTypeDialog
        v-model="paymentTypeDialog"
        :quote="quote"
        :version="quote.versions[selectedVersion]"
        :version-index="selectedVersion"
        :order="order"
      />
    </div>
  </div>
</template>

<script>
import uid from 'uid'
import * as MaterialResolver from '@/js/MaterialResolver'
import * as WorktopResolver from '@/js/WorktopResolver'
import QuoteView from '@/views/QuoteView'
import MegaCad from '@/components/MegaCad'
import CustomerCad from '@/components/CustomerCad'
import MegaTable from '@/components/quoting/megaTable'
import Quote from '@/components/quoting/quote'
import RecutTracker from '@/components/RecutTracker'
import Scanner from '@/components/quoting/Scanner'
import SetPaymentTypeDialog from '@/components/SetPaymentTypeDialog'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import AnalyticsSettings from '@/js/analytics-settings'
import { auth, db, functions } from '@/js/db'
import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'QuoteEdit',
  components: {
    MegaTable,
    Quote,
    QuoteView,
    MegaCad,
    CustomerCad,
    RecutTracker,
    Scanner,
    SetPaymentTypeDialog,
    LoadingOverlay
  },
  props: ['quoteID', 'versionID', 'shipment'],
  data() {
    return {
      versionAction: null,
      versionOrders: {}, // Store order data for all versions
      tabs: [
        {
          title: 'Quote',
          tabComponent: 'Quote'
        },
        {
          title: 'Fronts',
          tabComponent: 'MegaTable',
          itemComponent: 'Front',
          itemType: 'fronts',
          labels: [
            { span: 1, title: 'Type' },
            { span: 1, title: 'Item' },
            { span: 1, title: 'Options' },
            { span: 1, title: 'W' },
            { span: 1, title: 'H' },
            { span: 1, title: 'Qty' },
            { span: 2, title: 'Material' },
            { span: 2, title: 'Handle' },
            { span: 2, title: 'Actions' }
          ]
        },
        {
          title: 'Cover Panels',
          tabComponent: 'MegaTable',
          itemComponent: 'CoverPanel',
          itemType: 'coverPanels',
          labels: [
            { span: 2, title: 'Type' },
            { span: 1, title: 'Location' },
            { span: 2, title: 'Material' },
            { span: 1, title: 'Options' },
            { span: 1, title: 'Width' },
            { span: 1, title: 'Height' },
            { span: 2, title: 'Lacquer' },
            { span: 1, title: 'Qty' },
            { span: 1, title: 'Actions' }
          ]
        },
        {
          title: 'Spacer Panels',
          tabComponent: 'MegaTable',
          itemComponent: 'SpacerPanel',
          itemType: 'spacerPanels',
          labels: [
            { span: 3, title: 'Location' },
            { span: 3, title: 'Material' },
            { span: 2, title: 'Length' },
            { span: 1, title: 'Qty' },
            { span: 1, title: 'Options' },
            { span: 2, title: 'Actions' }
          ]
        },
        {
          title: 'Worktops',
          tabComponent: 'MegaTable',
          itemComponent: 'Worktop',
          itemType: 'worktops',
          labels: [
            { span: 2, title: 'Material' },
            { span: 2, title: 'Thickness' },
            { span: 2, title: 'Colour' },
            { span: 2, title: 'Faces' },
            { span: 2, title: 'Size' },
            { span: 2, title: 'Actions' }
          ]
        },
        {
          // excludeUS: true,
          title: 'Urtils',
          tabComponent: 'MegaTable',
          itemComponent: 'Urtil',
          itemType: 'urtils',
          labels: [
            { span: 1, title: 'Name' },
            { span: 1, title: 'Type' },
            { span: 1, title: 'Carcass' },
            { span: 1, title: 'Back' },
            { span: 2, title: 'Options' },
            { span: 1, title: 'W' },
            { span: 1, title: 'H' },
            { span: 1, title: 'D' },
            { span: 1, title: 'Door Material 1' },
            { span: 1, title: 'Door Material 2' },
            { span: 1, title: 'Actions' }
          ]
        },
        {
          excludeUS: true,
          title: 'Pantry',
          tabComponent: 'MegaTable',
          itemComponent: 'Pantry',
          itemType: 'pantries',
          labels: [
            { span: 1, title: 'Name' },
            { span: 1, title: 'Type' },
            { span: 1, title: 'Hinge' },
            { span: 2, title: 'Carcass' },
            { span: 1, title: 'Back' },
            { span: 1, title: 'Options' },
            { span: 1, title: 'W' },
            { span: 1, title: 'H' },
            { span: 1, title: 'D' },
            { span: 1, title: 'Base Material' },
            { span: 1, title: 'Actions' }
          ]
        },
        {
          excludeUS: true,
          title: 'Spice Rack',
          tabComponent: 'MegaTable',
          itemComponent: 'SpiceRack',
          itemType: 'spiceRacks',
          labels: [
            { span: 1, title: 'Name' },
            { span: 1, title: 'Size' },
            { span: 1, title: 'Dowel' },
            { span: 2, title: 'Material Type' },
            { span: 2, title: 'Frame' },
            { span: 1, title: 'W' },
            { span: 1, title: 'H' },
            { span: 1, title: 'D' },
            { span: 1, title: 'Shelves' },
            { span: 1, title: 'Actions' }
          ]
        }
      ],
      currentTab: 0,
      scannerOverlayVisible: false,
      editNotesDialog: false,
      renameDialog: false,
      editOrderDialog: false,
      editOrderShipments: [],
      deleteVersionDialog: false,
      versionCheckDialog: false,
      localReadyToCheck: false,
      localVersionCheckComplete: false,
      localBypassVersionCheck: false,
      versionCheckSaving: false,
      editVersionOrderDialog: false,
      paymentTypeDialog: false,
      editVersionOrderForm: {
        creatorEmail: '',
        customerEmail: '',
        confirmCustomerEmail: ''
      },
      editingOrderData: null,
      orderNumber: null,
      tmpValue: null,
      tmpDesc: null,
      autoSaveFrequency: 2000,
      unsavedChanges: false,
      savingQuote: false,
      taskSyncTimer: null,
      taskSyncQueuedOrderID: null,
      syncingTasks: false,
      taskSyncDelay: 15000,
      taskSpecBaseline: null,
      refreshFxDialog: false,
      pendingFxRate: null,
      fetchingFxRate: false,
      quote: null,
      order: null,
      selectedVersion: null,
      draftPricing: null,
      draftMaterialUsage: null,
      draftMu: null,
      priceChangeDialog: false,
      generatingInvoice: false,
      recutDialog: false,
      visualOverlayVisible: false,
      visualBlockedDialog: false,
      visualBlockedReasons: [],
      visualSaving: false,
      autoSaveInterval: null
    }
  },
  computed: {
    creatorEmailOptions() {
      return AnalyticsSettings.quoteTeam.map((u) => u.email)
    },
    materialHandleSet() {
      return (
        this.quote.versions[this.selectedVersion].materials.length > 0 &&
        this.quote.versions[this.selectedVersion].handles.length > 0
      )
    },
    orderFullyPaid() {
      if (!this.order) return false
      if (this.order.balanceOutstanding) return false
      const total = this.$utils.toFixedNumber(this.order.total || 0, 2)
      const paymentsTotal = (this.order.payments || []).reduce(
        (sum, p) => sum + this.$utils.toFixedNumber(p.amount || 0, 2),
        0
      )
      return this.$utils.toFixedNumber(paymentsTotal, 2) >= total
    },
    currentFxRateDisplay() {
      const v = this.quote && this.selectedVersion != null ? this.quote.versions[this.selectedVersion] : null
      if (!v || !v.exchangeRates || !v.exchangeRates.rates) return '—'
      const rate = v.exchangeRates.rates[v.currency]
      if (rate == null) return '—'
      return `1 GBP = ${rate.toFixed(4)} ${v.currency}`
    },
    currentFxRateDate() {
      const v = this.quote && this.selectedVersion != null ? this.quote.versions[this.selectedVersion] : null
      if (!v || !v.exchangeRates || !v.exchangeRates.date) return 'no date'
      return v.exchangeRates.date
    },
    pendingFxRateDisplay() {
      const v = this.quote && this.selectedVersion != null ? this.quote.versions[this.selectedVersion] : null
      if (!this.pendingFxRate || !this.pendingFxRate.rates || !v) return 'fetching…'
      const rate = this.pendingFxRate.rates[v.currency]
      if (rate == null) return '—'
      return `1 GBP = ${rate.toFixed(4)} ${v.currency}`
    },
    pendingFxRateDate() {
      if (!this.pendingFxRate || !this.pendingFxRate.date) return ''
      return this.pendingFxRate.date
    },
    // used for version select list - TODO version select list can contain whole object as result rather than key... probably better approach
    versionNames() {
      let a = []
      for (let i = 0; i < this.quote.versions.length; i++) {
        let version = this.quote.versions[i]
        let name =
          version.title == null
            ? this.$moment(this.$utils.ensureFirestoreTimestamp(version.date).toDate()).format('Do MMM YYYY H:mma')
            : version.title
        let orderData = this.versionOrders[version.id]

        // If orderData is not loaded yet, check if this is the currently selected version
        if (
          !orderData &&
          this.selectedVersion !== null &&
          this.quote.versions[this.selectedVersion].id === version.id &&
          this.order
        ) {
          orderData = this.order
        }

        let hasPayment = orderData && ((orderData.payments && orderData.payments.length > 0) || version.foc)
        a.push({
          text: name,
          value: i,
          hasPayment: hasPayment,
          readyToCheck: version.readyToCheck || false,
          versionCheckComplete: version.versionCheckComplete || version.bypassVersionCheck || false
        })
      }
      return a
    },
    publishedComponent() {
      let c
      switch (this.currentTab) {
        case 0:
          c = 'QuoteView'
          break
        case 1:
          c = 'CustomerCad'
          break
        case 2:
          c = 'MegaCad'
          break
      }
      return c
    },
    confirmEmailErrors() {
      const { customerEmail, confirmCustomerEmail } = this.editVersionOrderForm
      if (confirmCustomerEmail && customerEmail !== confirmCustomerEmail) {
        return ['Email addresses must match exactly']
      }
      return []
    },
    canSaveVersionOrder() {
      if (!this.editVersionOrderForm.creatorEmail) return false
      if (this.editingOrderData) {
        if (!this.editVersionOrderForm.customerEmail || !this.editVersionOrderForm.confirmCustomerEmail) return false
        return this.editVersionOrderForm.customerEmail === this.editVersionOrderForm.confirmCustomerEmail
      }
      return true
    },
    storeDataReady() {
      return this.$store.state.handles !== null && this.$store.state.pricing !== null
    }
  },
  created() {
    this.bindQuoteFromID()
    // setup periodic autosave
    this.autoSaveInterval = setInterval(this.saveIfRequired, this.autoSaveFrequency)
  },
  watch: {
    quote: {
      handler(to, from) {
        if (to !== null) {
          // legacy
          let legacyBackfilled = false
          if (!this.quote.region) {
            this.quote.region = 'uk'
            this.quote.versions[this.selectedVersion].deliveryLocation = null
            legacyBackfilled = true
          }
          if (from != null) {
            this.unsavedChanges = true
          }
          if (from == null || legacyBackfilled || this.taskSpecBaseline === null) {
            this.resetTaskSpecBaseline()
          }
          // bind if new order added for current version
          if (from && this.selectedVersion !== null && this.quote.orders) {
            const versionID = this.quote.versions[this.selectedVersion].id
            if ((!from.orders || !from.orders[versionID]) && to.orders[versionID]) {
              this.getVersionOrder()
            }
          }
        }
      },
      deep: true
    },
    quoteID() {
      this.bindQuoteFromID()
    },
    selectedVersion() {
      this.getVersionOrder()
      this.resetTaskSpecBaseline()
    },
    versionCheckDialog(val) {
      if (val) {
        const version = this.quote?.versions?.[this.selectedVersion]
        this.localBypassVersionCheck = version?.bypassVersionCheck || false
        this.localReadyToCheck = version?.readyToCheck || false
        this.localVersionCheckComplete = this.localReadyToCheck ? version?.versionCheckComplete || false : false
      }
    },
    versionOrders: {
      handler() {
        // Force re-evaluation of versionNames when versionOrders changes
        this.$forceUpdate()
      },
      deep: true
    }
  },
  beforeUnmount() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval)
    }
    this.flushTaskSync()
  },
  methods: {
    openVisualTab(validationResult) {
      // Called from Quote component with validation result
      if (validationResult && !validationResult.canOpen) {
        this.visualBlockedReasons = validationResult.reasons || []
        this.visualBlockedDialog = true
        return
      }
      this.visualOverlayVisible = true
    },
    onScannerConverted() {
      this.scannerOverlayVisible = false
      this.unsavedChanges = true
      this.currentTab = 1
    },
    tryOpenVisual() {
      if (this.currentTab === 0 && this.$refs.currentTabComponent?.checkAndOpenVisual) {
        this.$refs.currentTabComponent.checkAndOpenVisual()
      } else {
        // If not on Quote tab, we can't validate - just try to open
        this.visualOverlayVisible = true
      }
    },
    async closeVisualOverlay() {
      // Save layout before closing
      if (this.$refs.visualCad) {
        this.visualSaving = true
        try {
          await this.$refs.visualCad.save()
        } catch (e) {
          console.error('Error saving visual layout:', e)
        }
        this.visualSaving = false
      }
      this.visualOverlayVisible = false
    },
    handleReadyToCheckToggle() {
      if (!this.localReadyToCheck) {
        this.localVersionCheckComplete = false
      }
    },
    handleBypassToggle() {
      if (this.localBypassVersionCheck) {
        this.localReadyToCheck = false
        this.localVersionCheckComplete = false
      }
    },
    async handleVersionCheckSave() {
      if (!this.quote || this.selectedVersion === null) return
      try {
        this.versionCheckSaving = true
        const version = this.quote.versions[this.selectedVersion]

        // Apply bypass to version
        version.bypassVersionCheck = this.localBypassVersionCheck

        // If bypassed, clear check flags (allowOrder can be toggled manually)
        if (this.localBypassVersionCheck) {
          version.readyToCheck = false
          version.versionCheckComplete = false
        } else {
          // Apply locals to version
          version.readyToCheck = this.localReadyToCheck
          version.versionCheckComplete = this.localReadyToCheck ? this.localVersionCheckComplete : false
        }

        // Maintain checkReqVersions map
        if (!this.quote.checkReqVersions) {
          this.quote.checkReqVersions = {}
        }

        if (version.readyToCheck) {
          this.quote.checkReqVersions[version.id] = {
            id: version.id,
            title: version.title,
            total: version.pricing?.total || 0
          }
        } else if (this.quote.checkReqVersions[version.id]) {
          delete this.quote.checkReqVersions[version.id]
        }

        // If check complete, ensure removal from requests
        if (version.versionCheckComplete && this.quote.checkReqVersions[version.id]) {
          delete this.quote.checkReqVersions[version.id]
        }

        await updateDoc(doc(db, 'quotes', this.quoteID), {
          checkReqVersions: this.quote.checkReqVersions,
          versions: this.quote.versions
        })

        this.unsavedChanges = false
        this.versionCheckDialog = false
      } catch (error) {
        this.$store.commit('openSnackbar', { snackbarText: 'Error saving: ' + error.message, timeout: 5000 })
      } finally {
        this.versionCheckSaving = false
      }
    },
    ondraftQuoteUpdated(pricing, materialUsage, mu) {
      this.draftPricing = pricing
      this.draftMaterialUsage = materialUsage
      this.draftMu = mu
    },
    getVersionIndexPosFromID(ID) {
      for (let i = 0; i < this.quote.versions.length; i++) {
        if (this.quote.versions[i].id == ID) {
          return i
        }
      }
    },
    async getVersionOrder() {
      if (this.selectedVersion == null) return

      const version = this.quote.versions[this.selectedVersion]
      const versionID = version.id
      if (this.quote.orders && this.quote.orders[versionID]) {
        await this.$live('order', doc(db, 'orders', this.quote.orders[versionID].orderID))
        if (this.order) {
          const matDirty = await MaterialResolver.convertOnReadForOrder(version, this.order.dateCreated)
          const wtDirty = await WorktopResolver.convertOnReadForOrder(
            version,
            this.quote.region,
            this.order.dateCreated
          )
          if (matDirty || wtDirty) {
            await updateDoc(doc(db, 'quotes', this.quoteID), { versions: this.quote.versions })
          }
        }
      } else {
        this.order = null
        if (version.published && version.publishedTime) {
          const matDirty = await MaterialResolver.convertOnReadForPublished(version, version.publishedTime)
          const wtDirty = await WorktopResolver.convertOnReadForPublished(
            version,
            this.quote.region,
            version.publishedTime
          )
          if (matDirty || wtDirty) {
            await updateDoc(doc(db, 'quotes', this.quoteID), { versions: this.quote.versions })
          }
        }
      }

      await MaterialResolver.prefetchMaterialsForVersion(version)
      await WorktopResolver.prefetchWorktopsForVersion(version)
    },
    async loadAllVersionOrders() {
      if (!this.quote || !this.quote.orders) return

      this.versionOrders = {}

      // Load order data for each version
      for (const version of this.quote.versions) {
        if (this.quote.orders[version.id]) {
          try {
            const orderDoc = await getDoc(doc(db, 'orders', this.quote.orders[version.id].orderID))
            if (orderDoc.exists()) {
              this.versionOrders[version.id] = orderDoc.data()
            }
          } catch (error) {
            console.error('Error loading order for version', version.id, ':', error)
          }
        }
      }
    },
    bindQuoteFromID() {
      this.$live('quote', doc(db, 'quotes', this.quoteID)).then((quote) => {
        // Set page title if we're NOT in an overlay
        if (!this.shipment) {
          this.$store.commit('setPageTitle', quote.customerEmail)
        }
        if (!this.selectedVersion) {
          this.selectedVersion = this.versionID ? this.getVersionIndexPosFromID(this.versionID) : 0
        }
        this.getVersionOrder()
        this.loadAllVersionOrders() // Load order data for all versions
      })
    },
    onTabChange(val) {
      this.currentTab = val
      this.$store.commit('clearSelectAndFocus')
    },
    async saveIfRequired() {
      // TODO think it would be better to use update just to update the version not the whole quote
      if (this.savingQuote || !this.unsavedChanges) return
      this.savingQuote = true

      try {
        await setDoc(doc(db, 'quotes', this.quoteID), this.quote)
        this.unsavedChanges = false

        if (this.order && this.taskSpecChanged()) this.queueTaskSync()
      } catch (error) {
        // this.$store.commit('openSnackbar', { snackbarText: 'Error Saving' })
        console.log('Error', error)
      } finally {
        this.savingQuote = false
      }
    },
    taskSpecFingerprint() {
      const version = this.quote?.versions?.[this.selectedVersion]
      if (!version) return null

      const SPEC_KEYS = [
        'materials',
        'fronts',
        'coverPanels',
        'urtils',
        'spiceRacks',
        'pantries',
        'worktops',
        'earlyWorktops',
        'spacerPanels',
        'earlySpacers',
        'deliveryLocation',
        'deliveryOverride'
      ]

      const stable = (value) => {
        if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
        if (Array.isArray(value)) return '[' + value.map(stable).join(',') + ']'
        return (
          '{' +
          Object.keys(value)
            .sort()
            .map((key) => JSON.stringify(key) + ':' + stable(value[key]))
            .join(',') +
          '}'
        )
      }

      return stable(SPEC_KEYS.map((key) => version[key] ?? null))
    },
    resetTaskSpecBaseline() {
      const versionID = this.quote?.versions?.[this.selectedVersion]?.id ?? null
      this.taskSpecBaseline = { versionID, fingerprint: this.taskSpecFingerprint() }
    },
    taskSpecChanged() {
      const versionID = this.quote?.versions?.[this.selectedVersion]?.id ?? null
      const fingerprint = this.taskSpecFingerprint()
      if (fingerprint === null) return false

      if (!this.taskSpecBaseline || this.taskSpecBaseline.versionID !== versionID) {
        this.taskSpecBaseline = { versionID, fingerprint }
        return false
      }

      return this.taskSpecBaseline.fingerprint !== fingerprint
    },
    queueTaskSync() {
      const orderID = this.quote?.orders?.[this.quote?.versions?.[this.selectedVersion]?.id]?.orderID
      if (!orderID) return

      if (this.taskSyncQueuedOrderID && this.taskSyncQueuedOrderID !== orderID) {
        this.flushTaskSync()
      }

      this.taskSyncQueuedOrderID = orderID
      if (this.taskSyncTimer) clearTimeout(this.taskSyncTimer)
      this.taskSyncTimer = setTimeout(this.flushTaskSync, this.taskSyncDelay)
    },
    async flushTaskSync() {
      if (this.taskSyncTimer) {
        clearTimeout(this.taskSyncTimer)
        this.taskSyncTimer = null
      }
      if (!this.taskSyncQueuedOrderID) return

      if (this.syncingTasks) {
        this.taskSyncTimer = setTimeout(this.flushTaskSync, 2000)
        return
      }

      const orderID = this.taskSyncQueuedOrderID
      this.taskSyncQueuedOrderID = null

      const syncedVersionID = this.quote?.versions?.[this.selectedVersion]?.id ?? null
      const syncedFingerprint = this.taskSpecFingerprint()

      this.syncingTasks = true
      try {
        const updateTasksForOrderFunc = httpsCallable(functions, 'updateTasksForOrder')
        await updateTasksForOrderFunc(orderID)
        this.taskSpecBaseline = { versionID: syncedVersionID, fingerprint: syncedFingerprint }
      } catch (error) {
        console.log('Error syncing tasks', error)
      } finally {
        this.syncingTasks = false
      }
    },
    saveVisualId(id) {
      this.quote.versions[this.selectedVersion].customerVisualID = id
      this.unsavedChanges = true
    },
    handleVisualLoadError(message) {
      this.visualOverlayVisible = false
      this.$store.commit('openSnackbar', { snackbarText: message, timeout: 5000 })
    },
    async duplicateSelectedVersion() {
      if (this.versionAction) return
      this.versionAction = 'Copying version...'
      try {
        await this.copyVersion()
      } catch (e) {
        console.error('Failed to duplicate version:', e)
        this.$store.commit('openSnackbar', {
          snackbarText: 'Could not copy version: ' + e.message,
          timeout: 5000
        })
      } finally {
        this.versionAction = null
      }
    },
    async copyVersion() {
      let clone = Object.assign({}, this.quote.versions[this.selectedVersion])
      clone.id = uid(10)
      clone.date = this.$utils.tsNow()
      clone.published = false
      clone.title = 'Copy of ' + clone.title
      clone.creatorEmail = auth.currentUser.email
      clone.foc = false
      clone.allowOrder = false
      clone.readyToCheck = false
      clone.versionCheckComplete = false
      clone.bypassVersionCheck = false
      delete clone.pricing
      delete clone.cockupID
      delete clone.mu

      clone.materials = (clone.materials || []).map((m) => ({ ...m }))
      clone.worktops = (clone.worktops || []).map((w) => ({ ...w }))

      // clone visual if exists
      if (clone.customerVisualID) {
        const visualSnap = await getDoc(doc(db, 'visuals', clone.customerVisualID))
        const newVisual = doc(collection(db, 'visuals'))
        await setDoc(newVisual, {
          json: visualSnap.data().json
        })
        clone.customerVisualID = newVisual.id
      }

      this.quote.versions.push(clone)
      this.selectedVersion = this.quote.versions.length - 1

      this.$store.commit('openSnackbar', {
        snackbarText: 'Duplicate version created',
        timeout: 5000
      })
    },
    async addEmptyVersion() {
      if (this.versionAction) return
      this.versionAction = 'Creating version...'
      try {
        await this.createEmptyVersion()
      } catch (e) {
        console.error('Failed to add version:', e)
        this.$store.commit('openSnackbar', {
          snackbarText: 'Could not create version: ' + e.message,
          timeout: 5000
        })
      } finally {
        this.versionAction = null
      }
    },
    async createEmptyVersion() {
      let exchangeRates = null
      try {
        const getExchangeRates = httpsCallable(functions, 'getExchangeRates')
        const fxResult = await getExchangeRates({ base: 'GBP', symbols: ['EUR', 'USD'] })
        exchangeRates = fxResult.data
      } catch (e) {
        exchangeRates = null
      }

      this.quote.versions.push({
        id: uid(10),
        creatorEmail: auth.currentUser.email,
        published: false,
        title: 'Version ' + (this.quote.versions.length + 1),
        date: this.$utils.tsNow(),
        fronts: [],
        coverPanels: [],
        spacerPanels: [],
        materials: [],
        urtils: [],
        pantries: [],
        spiceRacks: [],
        handles: [],
        worktops: [],
        discount: 0,
        foc: false,
        readyToCheck: false,
        versionCheckComplete: false,
        bypassVersionCheck: false,
        currency: this.quote.versions[this.quote.versions.length - 1].currency,
        exchangeRates
      })
      this.selectedVersion = this.quote.versions.length - 1

      this.$store.commit('openSnackbar', {
        snackbarText: 'New version created',
        timeout: 5000
      })
    },
    async openRefreshFxDialog() {
      this.fetchingFxRate = true
      this.pendingFxRate = null
      try {
        const getExchangeRates = httpsCallable(functions, 'getExchangeRates')
        const fxResult = await getExchangeRates({ base: 'GBP', symbols: ['EUR', 'USD'], force: true })
        this.pendingFxRate = fxResult.data
        this.refreshFxDialog = true
      } catch (e) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Failed to fetch live exchange rate',
          timeout: 5000
        })
      } finally {
        this.fetchingFxRate = false
      }
    },
    confirmRefreshExchangeRate() {
      if (!this.pendingFxRate) return
      this.quote.versions[this.selectedVersion].exchangeRates = this.pendingFxRate
      this.unsavedChanges = true
      this.refreshFxDialog = false
      this.pendingFxRate = null
      this.$store.commit('openSnackbar', {
        snackbarText: 'Exchange rate refreshed',
        timeout: 4000
      })
    },
    deleteVersion() {
      if (this.quote.versions[this.selectedVersion].cockupID) {
        deleteDoc(doc(db, 'cockUps', this.quote.versions[this.selectedVersion].cockupID))
      }

      this.quote.versions.splice(this.selectedVersion, 1)
      this.selectedVersion = this.selectedVersion > 0 ? this.selectedVersion - 1 : 0
      this.deleteVersionDialog = false
      this.$store.commit('openSnackbar', {
        snackbarText: 'Version deleted'
      })
    },
    async publishSelectedVersion() {
      let v = this.quote.versions[this.selectedVersion]
      let errors = false
      let s = ''

      for (let material of v.materials) {
        // Skip empty materials (just id) - handled by unused materials check
        if (Object.keys(material).length <= 1) continue

        const resolved = MaterialResolver.resolveMaterial(material)
        const sizeOffered =
          resolved &&
          (resolved.sizes || []).some((sz) => sz.thickness == material.thickness && `${sz.w}x${sz.h}` === material.size)

        if (
          !material.value ||
          !resolved ||
          !material.thickness ||
          !material.size ||
          !sizeOffered ||
          (!resolved.hasGrain && resolved.colours !== undefined && !material.colourCode) ||
          (!resolved.hasGrain && !material.sides) // Laminates require sides
        ) {
          errors = true
          s += 'Incomplete material in settings. '
          break
        }
      }

      // check for unused materials
      for (let material of v.materials) {
        let materialInHandles
        let materialInFronts
        let materialInCoverPanels
        let materialInUrtils
        let materialInPantries
        let materialInSpiceRacks

        if (v.handles) materialInHandles = v.handles.find((handle) => handle.insert == material.id)
        if (v.fronts) materialInFronts = v.fronts.find((front) => front.material == material.id)
        if (v.coverPanels)
          materialInCoverPanels = v.coverPanels.find((coverPanel) => coverPanel.material == material.id)
        if (v.urtils)
          materialInUrtils = v.urtils.find(
            (urtil) =>
              urtil.carcass == material.id ||
              urtil.back == material.id ||
              urtil.material1 == material.id ||
              urtil.material2 == material.id
          )
        if (v.pantries)
          materialInPantries = v.pantries.find(
            (pantry) => pantry.carcass == material.id || pantry.base == material.id || pantry.back == material.id
          )
        if (v.spiceRacks) materialInSpiceRacks = v.spiceRacks.find((spiceRack) => spiceRack.frame == material.id)

        if (
          !materialInHandles &&
          !materialInFronts &&
          !materialInUrtils &&
          !materialInPantries &&
          !materialInCoverPanels &&
          !materialInSpiceRacks
        ) {
          errors = true
          s += `Delete Unused ${material.thickness}mm ${material.label}`
          // console.log('Unused', material)
          break
        }
      }

      // check all handles are complete
      for (let j = 0; j < v.handles.length; j++) {
        if (!v.handles[j].value || v.handles[j].orientation === null || v.handles[j].insert === null) {
          errors = true
          s += 'Incomplete handles. '
          break
        }
      }

      // check for unused handles
      for (let handle of v.handles) {
        let handleInFronts = v.fronts.find((front) => front.handle == handle.id)

        if (!handleInFronts) {
          errors = true
          s += `Delete Unused ${handle.label}`
          // console.log('Unused', handle)
          break
        }
      }

      // delivery location
      if (!v.deliveryLocation) {
        errors = true
        s += 'Delivery location required. '
      }

      if (!v.frontTypes) {
        errors = true
        s += 'Front types must be completed'
      }

      if (!v.grainDirection) {
        errors = true
        s += 'Grain direction required.'
      }

      // Ensure no empty additional items
      if (v.additionalItems) {
        for (const item of v.additionalItems) {
          if (item.description === null || item.value === null || item.value.length === 0) {
            s += 'Incomplete additional items'
            errors = true
          }
        }
      }

      if (errors) {
        this.$store.commit('openSnackbar', { snackbarText: s, timeout: 5000 })
      } else {
        const pricingChange = this.quote.versions[this.selectedVersion].pricing
          ? this.$utils.toFixedNumber(
              this.draftPricing.total - this.quote.versions[this.selectedVersion].pricing.total,
              2
            )
          : 0

        if (this.order && pricingChange) {
          this.priceChangeDialog = true
        } else {
          MaterialResolver.pinMaterialIdsOnVersion(v)
          WorktopResolver.pinWorktopIdsOnVersion(v, this.quote.region)
          v.pricing = this.draftPricing
          v.materialUsage = this.draftMaterialUsage
          v.mu = this.draftMu
          v.publishedTime = this.$utils.tsNow()
          v.published = true
          // if (v.draftPricing) delete v.draftPricing
          this.unsavedChanges = true
        }
      }

      if (v.cockupID) {
        updateDoc(doc(db, 'cockUps', v.cockupID), {
          value: v.pricing.total,
          foc: v.foc,
          versionID: v.id
        })
      }
    },
    async commitOrderChanges(updatePricing) {
      try {
        // Save changes on the quote
        let v = this.quote.versions[this.selectedVersion]

        if (updatePricing) {
          v.pricing = this.draftPricing
          v.materialUsage = this.draftMaterialUsage
          if (v.draftPricing) delete v.draftPricing
        } else {
          v.draftPricing = this.draftPricing
          v.draftMaterialUsage = this.draftMaterialUsage
        }

        v.publishedTime = this.$utils.tsNow()
        v.published = true

        // save manually as we need to complete before calling the task update function
        await setDoc(doc(db, 'quotes', this.quoteID), this.quote)

        // update the order...
        updateDoc(doc(db, 'orders', this.quote.orders[this.quote.versions[this.selectedVersion].id].orderID), {
          balanceOutstanding: this.order.foc ? false : true,
          total: v.pricing.total
        })

        this.priceChangeDialog = false
        return
      } catch (error) {
        console.log(error)
        this.$store.commit('openSnackbar', { snackbarText: error.message })
      }
    },
    async openEditOrderDialog() {
      this.editOrderShipments = []
      if (this.order && this.order.id) {
        const snap = await getDocs(query(collection(db, 'shipments'), where('orderID', '==', this.order.id)))
        this.editOrderShipments = snap.docs.map((d) => d.data())
      }
      this.editOrderDialog = true
    },
    unpublishSelectedVersion() {
      this.quote.versions[this.selectedVersion].publishedTime = null
      this.quote.versions[this.selectedVersion].published = false
    },
    copyCustomerQuoteLink() {
      let link =
        window.location.origin +
        '/#/customer/quote/' +
        this.quoteID +
        '/' +
        this.quote.versions[this.selectedVersion].id
      this.$utils.copyToClipboard(link)
      this.$store.commit('openSnackbar', {
        snackbarText: 'Customer quote link copied to clipboard',
        timeout: 5000
      })
    },
    copyInvoiceLink() {
      this.$utils.copyToClipboard(this.order.xeroInvoiceURL)
      this.$store.commit('openSnackbar', {
        snackbarText: 'Invoice link copied to clipboard',
        timeout: 5000
      })
    },
    async generateEuInvoice() {
      this.generatingInvoice = true

      const generateInvoiceFunction = httpsCallable(functions, 'generateInvoice')
      await generateInvoiceFunction({
        orderID: this.order.id
      })

      await this.getVersionOrder()

      this.$store.commit('openSnackbar', {
        snackbarText: 'Invoice Generated',
        timeout: 5000
      })
      this.generatingInvoice = false
    },
    updateVersionTitle() {
      const title = (this.tmpValue || '').trim()
      const desc = (this.tmpDesc || '').trim()
      this.quote.versions[this.selectedVersion].title = title
      this.quote.versions[this.selectedVersion].titleDesc = desc
      this.renameDialog = false
      this.tmpValue = null
      this.tmpDesc = null
    },
    updateNotes() {
      if (!this.quote.versions[this.selectedVersion].notes) {
        this.quote.versions[this.selectedVersion].notes = this.tmpValue
      } else {
        this.quote.versions[this.selectedVersion].notes = this.tmpValue
      }
      this.editNotesDialog = false
      this.tmpValue = null
    },
    recutComplete(cockupID, empty, lucidCoverCost) {
      let clone = Object.assign({}, this.quote.versions[this.selectedVersion])

      clone.recut = true
      clone.lucidCoverCost = lucidCoverCost
      clone.recutOriginalVersionID = clone.id
      clone.recutOriginalOrderID = this.quote.orders[clone.id].orderID

      clone.id = uid(10)
      clone.date = this.$utils.tsNow()
      clone.title = 'Recut of ' + clone.title
      clone.creatorEmail = auth.currentUser.email
      clone.recut = true
      clone.cockupID = cockupID
      clone.readyToCheck = false
      clone.versionCheckComplete = false
      clone.bypassVersionCheck = false

      clone.foc = false
      clone.allowOrder = false
      clone.allowDeposit = false
      clone.allowBankTransfer = false

      clone.published = false
      clone.publishedTime = null

      if (empty) {
        clone.fronts = []
        clone.coverPanels = []
        clone.spacerPanels = []
        clone.urtils = []
        clone.pantries = []
        clone.spiceRacks = []
        clone.worktops = []
      }

      delete clone.pricing
      delete clone.draftPricing
      delete clone.draftMaterialUsage
      delete clone.customerVisualID

      this.quote.versions.push(clone)
      this.selectedVersion = this.quote.versions.length - 1
      this.recutDialog = false

      this.$store.commit('openSnackbar', {
        snackbarText: 'Recut created',
        timeout: 5000
      })
    },
    placeOrder() {
      let routeData = this.$router.resolve({
        name: 'customerQuote',
        params: {
          quoteID: this.quote.id,
          versionID: this.quote.versions[this.selectedVersion].id
        }
      })
      window.open(routeData.href, '_blank')
    },
    printableSVG() {
      let routeData = this.$router.resolve({
        name: 'customerSVG',
        params: {
          quoteID: this.quoteID,
          versionID: this.quote.versions[this.selectedVersion].id,
          showGrain: true
        }
      })
      window.open(routeData.href, '_blank')
    },
    async editVersionOrderInfo() {
      const versionID = this.quote.versions[this.selectedVersion].id
      const version = this.quote.versions[this.selectedVersion]

      // Populate creator email
      this.editVersionOrderForm.creatorEmail = version.creatorEmail || ''

      // Check if there's an associated order
      if (this.quote.orders && this.quote.orders[versionID]) {
        const orderID = this.quote.orders[versionID].orderID

        try {
          // Fetch the order document
          const orderDoc = await getDoc(doc(db, 'orders', orderID))

          if (orderDoc.exists()) {
            this.editingOrderData = orderDoc.data()
            this.orderNumber = this.editingOrderData.orderNumber || orderID

            // Populate customer email fields
            this.editVersionOrderForm.customerEmail = this.editingOrderData.customerEmail || ''
            this.editVersionOrderForm.confirmCustomerEmail = ''
          } else {
            this.editingOrderData = null
            this.orderNumber = null
          }
        } catch (error) {
          this.$store.commit('openSnackbar', { snackbarText: 'Error fetching order: ' + error.message })
          this.editingOrderData = null
          this.orderNumber = null
        }
      } else {
        this.editingOrderData = null
        this.orderNumber = null
      }

      this.editVersionOrderDialog = true
    },
    async saveVersionOrderInfo() {
      if (!this.canSaveVersionOrder) return

      try {
        const versionID = this.quote.versions[this.selectedVersion].id

        // Update version creatorEmail
        this.quote.versions[this.selectedVersion].creatorEmail = this.editVersionOrderForm.creatorEmail

        // Update quote in database
        await updateDoc(doc(db, 'quotes', this.quoteID), {
          versions: this.quote.versions
        })

        if (this.editingOrderData && this.quote.orders[versionID]) {
          await updateDoc(doc(db, 'orders', this.quote.orders[versionID].orderID), {
            customerEmail: this.editVersionOrderForm.customerEmail,
            creatorEmail: this.editVersionOrderForm.creatorEmail
          })
        }

        // Close dialog
        this.editVersionOrderDialog = false

        // Reset form
        this.editVersionOrderForm = {
          creatorEmail: '',
          customerEmail: '',
          confirmCustomerEmail: ''
        }
        this.editingOrderData = null
        this.orderNumber = null
        this.$refs.editVersionOrderForm?.resetValidation()

        this.$store.commit('openSnackbar', { snackbarText: 'Version and order updated successfully' })
      } catch (error) {
        this.$store.commit('openSnackbar', { snackbarText: 'Error updating: ' + error.message })
      }
    }
  }
}
</script>

<style scoped>
#page-actions .v-btn--disabled {
  background-color: #e0e0e0 !important;
  color: #9e9e9e !important;
}
</style>
